import { useState, useRef, useEffect } from 'react';

interface UseLazyImageOptions {
  thumbnailSrc: string;
  fullSrc: string;
}

interface UseLazyImageReturn {
  currentSrc: string;
  isLoading: boolean;
  hasError: boolean;
  preloadFull: () => void;
}

export const useLazyImage = ({ 
  thumbnailSrc, 
  fullSrc 
}: UseLazyImageOptions): UseLazyImageReturn => {
  const [currentSrc, setCurrentSrc] = useState(thumbnailSrc);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [fullImageLoaded, setFullImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>();

  const preloadFull = () => {
    if (fullImageLoaded || isLoading) return;

    setIsLoading(true);
    setHasError(false);

    const img = new Image();
    imgRef.current = img;

    img.onload = () => {
      setCurrentSrc(fullSrc);
      setFullImageLoaded(true);
      setIsLoading(false);
    };

    img.onerror = () => {
      setHasError(true);
      setIsLoading(false);
    };

    img.src = fullSrc;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (imgRef.current) {
        imgRef.current.onload = null;
        imgRef.current.onerror = null;
      }
    };
  }, []);

  return {
    currentSrc,
    isLoading,
    hasError,
    preloadFull
  };
};